import { Link } from "react-router-dom";
import { Fingerprint, ArrowLeft } from "lucide-react";

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-serif-dl text-2xl tracking-tight text-[#1A1A1A]">{title}</h2>
      <div className="text-[#5C5A54] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen paper-texture" data-testid="terms-page">
      <header className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="terms-brand-link">
          <Fingerprint className="text-[#1B4D3E]" size={24} strokeWidth={1.6} />
          <span className="font-serif-dl text-xl font-semibold tracking-tight">Dap Ledger</span>
        </Link>
        <Link
          to="/"
          data-testid="terms-back-link"
          className="flex items-center gap-2 text-[#5C5A54] hover:text-[#1A1A1A] text-sm transition-colors"
        >
          <ArrowLeft size={15} /> Back
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        <p className="overline text-[#E07A5F]">Legal</p>
        <h1 className="font-serif-dl text-4xl sm:text-5xl tracking-tight mt-2">Terms of Service</h1>
        <p className="text-sm text-[#8A877F] mt-3">Last updated: June 2026</p>

        <div className="mt-6 rounded-md bg-[#F0EBE1] border border-[#DEDBD3] px-4 py-3 text-sm text-[#8f6b52]">
          This is placeholder text provided as a template. Please have it reviewed by
          qualified legal counsel before relying on it in production.
        </div>

        <div className="mt-10 space-y-10">
          <Section title="1. Acceptance of Terms">
            <p>
              By accessing or using Dap Ledger (the “Service”), you agree to be bound by these
              Terms of Service and all terms incorporated by reference. If you do not agree to
              these terms, you may not access or use the Service.
            </p>
          </Section>

          <Section title="2. Description of the Service">
            <p>
              Dap Ledger is a community honor system that lets two parties record and seal
              handshake agreements using dual biometric signatures and a tamper-evident ledger.
              The Service records the existence and status of agreements you enter; it does not
              provide legal, financial, or credit-reporting services.
            </p>
          </Section>

          <Section title="3. Eligibility & Accounts">
            <p>
              You must be at least 18 years old and capable of forming a binding contract to use
              the Service. You are responsible for maintaining the confidentiality of your account
              and for all activity that occurs under it. You agree to provide accurate information
              and to keep it current.
            </p>
          </Section>

          <Section title="4. Acceptable Use">
            <p>
              You agree not to misuse the Service, including by using it to defraud, harass, or
              deceive others; uploading unlawful content; attempting to tamper with the ledger or
              another user’s data; or interfering with the normal operation of the Service.
            </p>
          </Section>

          <Section title="5. Honor System & No Guarantee">
            <p>
              Dap Ledger records agreements between users but does not enforce them, guarantee
              performance, or mediate disputes except through the optional tools provided in the
              Service. Any obligations you create are between you and your counterparty. Your
              standing is private by default and is not a public credit score.
            </p>
          </Section>

          <Section title="6. Intellectual Property">
            <p>
              The Service, including its software, design, and content (excluding user-submitted
              content), is owned by Dap Ledger and its licensors and is protected by applicable
              intellectual-property laws. You retain ownership of the content you submit.
            </p>
          </Section>

          <Section title="7. Privacy">
            <p>
              Your use of the Service is also governed by our Privacy Policy, which explains how we
              collect, use, and protect your information. By using the Service you consent to that
              processing.
            </p>
          </Section>

          <Section title="8. Disclaimers">
            <p>
              The Service is provided “as is” and “as available” without warranties of any kind,
              whether express or implied, including warranties of merchantability, fitness for a
              particular purpose, and non-infringement.
            </p>
          </Section>

          <Section title="9. Limitation of Liability">
            <p>
              To the maximum extent permitted by law, Dap Ledger shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages, or any loss of
              profits or data, arising out of or related to your use of the Service.
            </p>
          </Section>

          <Section title="10. Termination">
            <p>
              We may suspend or terminate your access to the Service at any time, with or without
              cause or notice. Upon termination, the provisions of these terms that by their nature
              should survive will continue to apply.
            </p>
          </Section>

          <Section title="11. Changes to These Terms">
            <p>
              We may update these Terms from time to time. When we do, we will revise the “Last
              updated” date above. Your continued use of the Service after changes take effect
              constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              Questions about these Terms? Contact us at{" "}
              <span className="text-[#1B4D3E]">support@thedapledger.com</span>.
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}
